import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  ChartLegend,
  ChartLegendCount,
  ChartLegendItem,
  ChartWrapper,
  NoDataAvailable,
  Title,
  Wrapper,
} from './TrendingStyledComponents';
import { SeeEpisodesButton } from './SeeEpisodesButton';
import { BrandSafetyBarChart } from './BrandSafetyBarChart';
import { useOverviewData } from '@app/hooks/useOverviewData';
import { Loader } from '@components/Loader';
import {
  GARM_RISK_LEVEL_COLOR_MAPPER,
  useOverviewContext,
} from '@insights_module/context/OverviewContext';
import { AQLQueryValue } from '@insights_module/adc_ql/types';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLPublishedDateQuery,
} from '@insights_module/adc_ql/queries';
import { CategoryType, GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { objectToToken } from '@utils/commonUtils';

export const GarmCategoryChart = () => {
  const { t } = useTranslation();
  const { garmPerCategoryOverview, isLoadingBrandSafety, episodesCount } = useOverviewData();
  const { selectedBrandSafetyCategory, setEpisodesListModalData, selectedDateRange } =
    useOverviewContext();

  const category = garmPerCategoryOverview?.[selectedBrandSafetyCategory];

  const chartData = useMemo(
    () =>
      Object.entries(category || {})
        .reverse()
        .map(([riskLevel, value]) => ({
          key: riskLevel,
          value: (value / episodesCount) * 100,
          count: value,
          name: t(
            `insights.overview_page.brand_safety_section.garm_analysis_chart_levels.${riskLevel}`
          ),
          fill: GARM_RISK_LEVEL_COLOR_MAPPER[
            riskLevel as keyof typeof GARM_RISK_LEVEL_COLOR_MAPPER
          ],
        })),
    [category, episodesCount]
  );

  const handleSeeEpisodeList = (riskLevel: string) => {
    setEpisodesListModalData({
      category: selectedBrandSafetyCategory,
      riskLevel,
    });

    const query = { all: [] as AQLQueryValue[] };
    if (selectedBrandSafetyCategory && riskLevel) {
      query.all.push(
        new AQLBrandSafetyGarmQuery([
          new AQLBrandSafetyGarmFilterQuery(
            selectedBrandSafetyCategory as CategoryType,
            'eq',
            riskLevel as GarmRiskLevelType
          ),
        ])
      );
    }

    if (selectedDateRange.minDate && selectedDateRange.maxDate) {
      query.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate,
          lte: selectedDateRange.maxDate,
        })
      );
    }

    const url = `/insights/episodes?query_token=${objectToToken(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!selectedBrandSafetyCategory) return null;

  return (
    <Wrapper tw='pb-[12px]'>
      <Title>{t(`insights.garm_category.${selectedBrandSafetyCategory}`)}</Title>
      {isLoadingBrandSafety ? (
        <Loader size='md' theme='dark' />
      ) : !episodesCount ? (
        <NoDataAvailable>{t('insights.overview_page.no_data')}</NoDataAvailable>
      ) : (
        <ChartWrapper>
          <BrandSafetyBarChart height={150} data={chartData} margin={{ left: 50 }} />
          <ChartLegend>
            {chartData.map((data) => (
              <ChartLegendItem key={data.key}>
                <ChartLegendCount>{data.count + ' ep'}</ChartLegendCount>
                <SeeEpisodesButton
                  onClick={() => handleSeeEpisodeList(data.key)}
                  disabled={!data.count}
                  css={[!data.count && tw`opacity-50`]}
                />
              </ChartLegendItem>
            ))}
          </ChartLegend>
        </ChartWrapper>
      )}
    </Wrapper>
  );
};
